import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { IPromoCode } from '../models/promo-code';
import { filter, map } from 'rxjs/operators';
import { LocationStore } from './location.store';
import { DiscountTypeShorthandPipe } from '../pipes/discount-type-shorthand.pipe';
import { V2CurrencyPipe } from '../v2-components/pipes/v2-currency.pipe';
import { InvoiceStore } from './invoice.store';

@Injectable({
  providedIn: 'root'
})
export class PromoCodeStore {
  constructor(
    private locationStore: LocationStore,
    private discountTypeShorthandPipe: DiscountTypeShorthandPipe,
    private v2CurrencyPipe: V2CurrencyPipe,
    private invoiceStore: InvoiceStore
  ) {}

  private _currentUserAppliedPromoCode$ = new BehaviorSubject<IPromoCode | null>(
    null
  );
  readonly currentUserAppliedPromoCode$ = this._currentUserAppliedPromoCode$.asObservable();

  readonly defaultPromoCode$: Observable<
    string
  > = this.locationStore.currentLocation$.pipe(
    map((location) => location.defaultPromoCode ?? '')
  );

  readonly currentAppliedPromoCode$: Observable<IPromoCode | null> = combineLatest(
    [
      this.currentUserAppliedPromoCode$,
      this.defaultPromoCode$,
      this.invoiceStore.currentInvoice$
    ]
  ).pipe(
    map(([currentUserAppliedPromoCode, defaultPromoCode, currentInvoice]) => {
      if (currentUserAppliedPromoCode) {
        return currentUserAppliedPromoCode;
      } else if (currentInvoice?.promoCode) {
        return currentInvoice.promoCode;
      } else if (defaultPromoCode) {
        return { code: defaultPromoCode };
      } else {
        return null;
      }
    })
  );

  readonly promoCodeDiscountMessage$: Observable<
    string
  > = this.currentAppliedPromoCode$.pipe(
    filter((promoCode) => !!promoCode),
    map((promoCode) => {
      let message = `(${promoCode?.code})`;
      const promoCodeDiscount = promoCode && promoCode.discount;
      if (promoCodeDiscount && promoCodeDiscount.type) {
        const convertedType = this.discountTypeShorthandPipe.transform(
          promoCodeDiscount.type
        );
        switch (promoCodeDiscount.type) {
          case 'percent':
            message =
              '(' + promoCodeDiscount.value / 100 + convertedType + ' OFF)';
            break;

          case 'dollar':
            message =
              '(' +
              this.v2CurrencyPipe.transform(promoCodeDiscount.value) +
              ' OFF)';
            break;

          default:
            message = promoCodeDiscount.type;
            break;
        }
      }

      return message;
    })
  );

  private _isApplyingPromoCode = new BehaviorSubject(false);

  private _isUserPromoCodeApplied$ = new BehaviorSubject(false);
  readonly isUserPromoCodeApplied$ = this._isUserPromoCodeApplied$.asObservable();

  get isUserPromoCodeApplied(): boolean {
    return this._isUserPromoCodeApplied$.getValue();
  }

  set isUserPromoCodeApplied(value: boolean) {
    this._isUserPromoCodeApplied$.next(value);
  }

  readonly isPromoCodeApplied$: Observable<boolean> = combineLatest([
    this.isUserPromoCodeApplied$,
    this.defaultPromoCode$
  ]).pipe(
    map(
      ([isUserPromoCodeApplied, defaultPromoCode]) =>
        !!(isUserPromoCodeApplied || defaultPromoCode)
    )
  );

  setCurrentPromoCode(promoCode: IPromoCode): void {
    if (promoCode) {
      this._currentUserAppliedPromoCode$.next(promoCode);
    }
  }

  clearCurrentPromoCode(): void {
    this._currentUserAppliedPromoCode$.next(null);
    this._isUserPromoCodeApplied$.next(false);
  }

  setIsApplyingPromoCode(bool: boolean): void {
    this._isApplyingPromoCode.next(bool);
  }
}
